import { useQuery } from '@tanstack/react-query';
import {
  getBonusMalusStudentsData,
  getBonusMalusTeachersData
} from 'api/bonusMalusApi';
import { capitalize } from 'helpers/utils';
import Loading from 'pages/components/Loading';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import {
  Col,
  Modal,
  Row,
  ToggleButton,
  ToggleButtonGroup
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import PDFDownloadButton from '../components/PDFDownloadButton';

const BonusMalusModalItem = ({ item, validFor }) => (
  <Row className="gx-card mx-0 py-4 align-items-center border-top border-200">
    <Col xs={8}>
      <div className="d-flex align-items-center">
        <div className="flex-1">
          <h5 className="fs-9 mb-0">{item.name}</h5>
          <span className="fs-10 text-500">{validFor}</span>
        </div>
      </div>
    </Col>
    <Col xs={4}>
      <Row className="align-items-center">
        <span
          className={
            'text-center mb-0 fw-bold text-' +
            (item.value > 0 ? 'success' : 'danger')
          }
        >
          {item.value > 0 ? '+' : ''}
          {item.value}
        </span>
      </Row>
    </Col>
  </Row>
);

const BonusMalusModalList = ({ data, type, validFor }) => {
  return data[type].map(categoryItem => (
    <>
      {categoryItem.list.length > 0 && (
        <>
          <div className="row mx-0 py-3 align-items-center border-bottom border-200 text-center">
            <h5 className="mb-0">
              <strong>{categoryItem.category}</strong>
            </h5>
          </div>
          <Row className="gx-card mx-0 bg-200 text-900 fs-10 fw-semibold">
            <Col xs={8} className="py-2">
              {capitalize(type)}
            </Col>
            <Col xs={4}>
              <Row>
                <span className="text-center py-2">Punti</span>
              </Row>
            </Col>
          </Row>
          {categoryItem.list.map(bonusMalusItem => (
            <BonusMalusModalItem
              item={bonusMalusItem}
              validFor={validFor}
              key={bonusMalusItem.id}
            />
          ))}
        </>
      )}
    </>
  ));
};

const BonusMalusModal = ({ showModal, setShowModal, type }) => {
  if (!showModal) {
    return null; // Do not render anything if showModal is false, to avoid calling the API and showing the modal
  }

  const {
    data: studentsBonusMalusData,
    isLoading: isLoadingStudentsBMData,
    isError: isStudentsBMError,
    error: studentsBMError
  } = useQuery({
    queryKey: ['studentsBonusMalusData'],
    queryFn: getBonusMalusStudentsData
  });

  const {
    data: teachersBonusMalusData,
    isLoading: isLoadingTeachersBMData,
    isError: isTeachersBMError,
    error: teacherBMError
  } = useQuery({
    queryKey: ['teachersBonusMalusData'],
    queryFn: getBonusMalusTeachersData
  });

  if (isStudentsBMError || isTeachersBMError) {
    toast.error(studentsBMError.message || teacherBMError.message);
    setShowModal(false);
    return null;
  }

  const [currentTab, setCurrentTab] = useState(0);

  return (
    <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          {capitalize(type)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!showModal || isLoadingStudentsBMData || isLoadingTeachersBMData ? (
          <Loading />
        ) : (
          <>
            <div className="d-flex justify-content-center">
              <ToggleButtonGroup
                type="radio"
                name="bonmal"
                defaultValue={currentTab}
                onChange={e => setCurrentTab(e)}
              >
                <ToggleButton
                  id="selectStudents"
                  value={0}
                  variant="outline-primary"
                  style={{ width: '120px' }}
                >
                  Studenti
                </ToggleButton>
                <ToggleButton
                  id="selectTeachers"
                  value={1}
                  variant="outline-primary"
                  style={{ width: '120px' }}
                >
                  Professori
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            {currentTab === 0 ? (
              <BonusMalusModalList
                data={studentsBonusMalusData}
                type={type}
                validFor="Studenti"
              />
            ) : (
              <BonusMalusModalList
                data={teachersBonusMalusData}
                type={type}
                validFor="Professori"
              />
            )}
            <div className="p-2 mb-2 text-center">
              <PDFDownloadButton />
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

BonusMalusModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['bonus', 'malus']).isRequired
};

BonusMalusModalList.propTypes = {
  type: PropTypes.oneOf(['bonus', 'malus']).isRequired
};

BonusMalusModalItem.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired
  }).isRequired,
  validFor: PropTypes.oneOf(['Studenti', 'Professori']).isRequired
};

export default BonusMalusModal;
